<template>
  <card-container
    :id="pageId"
    :is-loading="isLoading"
    :no-action="!hasCustomerInfo"
    hide-reset
    loading-message="Sending Request for Emergency Ride..."
    @submit="submitForm"
  >
    <template #title>
      Emergency Ride Request
    </template>

    <template #content>
      <template v-if="hasCustomerInfo">
        <div class="row">
          <div class="col-6">
            <label class="control-label text-left"> Customer Name </label>
            <div class="field-value">
              {{ customerFullName }}
            </div>
          </div>

          <div class="col-6">
            <label class="control-label text-left"> Phone Number </label>
            <div class="field-value">
              {{ customerPhoneNumber }}
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-6">
            <label class="control-label text-left"> Account Creation Date </label>

            <div class="field-value">
              {{ customerInfo?.createdAt || '' }}
            </div>
          </div>

          <div class="col-6">
            <label class="control-label text-left"> Number of Trips Taken </label>

            <div class="field-value">
              {{ totalOrderInfo?.totalRides || 0 }}
            </div>
          </div>
        </div>

        <div
          v-if="unpaidServices.length > 0"
          class="row"
        >
          <div class="col-6">
            <label class="control-label text-left"> Total Number of Unpaid Services </label>

            <div class="field-value">
              {{ unpaidServices.length }}
            </div>
          </div>

          <div class="col-6">
            <label class="control-label text-left"> Total Amount of Unpaid Services </label>

            <div class="field-value">
              ${{ owedTotalAmount }}
            </div>
          </div>
        </div>

        <div class="row">
          <div
            v-if="hasEmergencyRides"
            class="col-6"
          >
            <label class="control-label text-left"> Number of Emergency Rides Customer has Taken </label>

            <div class="field-value">
              {{ customerInfo.emergencyRides }}
            </div>
          </div>
        </div>

        <div class="row align-items-end">
          <div class="col-6">
            <label class="control-label text-left"> Did we take the customer to where they are currently at? </label>

            <div>
              <dropdown-list
                v-model="formData.isCustomerTakenToCurrentLocationByUs"
                :options="yesNoOptions"
                required
              />
            </div>
          </div>

          <div class="col-6">
            <label class="control-label text-left"> Are they going home? </label>

            <div>
              <dropdown-list
                v-model="formData.goingHome"
                :options="yesNoOptions"
                required
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-6">
            <label class="control-label text-left"> Did you ask for another form of payment? </label>

            <div>
              <dropdown-list
                v-model="formData.askedAnotherForm"
                :options="yesNoOptions"
                required
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-12">
            <label class="control-label text-left"> Reason for emergency ride* </label>

            <div>
              <sanitized-textarea
                v-model="formData.emergencyRideReason"
                autosize
                required
              />
            </div>
          </div>
        </div>
      </template>

      <h4
        v-else
        class="warning-message text-center mt-0"
      >
        Please pull up customer info on RightPanel.
      </h4>
    </template>
  </card-container>
</template>

<script setup lang="ts">
import { computed, ref, Ref } from 'vue';

import util from '@/Utils/util';
import { useProfile } from '@/use/profile';
import { useCustomer } from '@/use/customer';
import { sendPost } from '@/Utils/api';
import { useSwalDialog } from '@/use/swalDialog';
import { SlackFieldType, SlackPostDataType } from '@/models/slackData';
import { yesNoOptions } from '@/constants/app';

const pageId = 'emergency-ride-request';

const { userFullName } = useProfile();
const { customerInfo, customerPhoneNumber, customerFullName, totalOrderInfo } = useCustomer();
const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();

const formData: Ref<any> = ref({
  isCustomerTakenToCurrentLocationByUs: '',
  goingHome: '',
  askedAnotherForm: '',
  emergencyRideReason: '',
});
const isLoading = ref(false);

const hasCustomerInfo = computed(() => !!customerInfo.value?.id);
const hasEmergencyRides = computed(() => !!customerInfo.value?.emergencyRides);

const unpaidServices = computed(() => {
  if (!hasCustomerInfo.value) {
    return [];
  }

  const unpaidRides = customerInfo.value.unpaidRides || [];
  const unpaidGroceries = customerInfo.value.unpaidGroceries || [];
  const unpaidGourmetOrders = customerInfo.value.unpaidGourmetOrders || [];

  return [...unpaidRides, ...unpaidGroceries, ...unpaidGourmetOrders];
});

const owedTotalAmount = computed(() => {
  let totalAmount = 0;

  for (const unpaidService of unpaidServices.value) {
    totalAmount += Number(unpaidService.unpaidCost);
  }

  return totalAmount.toFixed(2);
});

const submitForm = async () => {
  const fields: SlackFieldType[] = [
    {
      title: 'Customer Name',
      value: customerFullName.value,
      short: true,
    },
    {
      title: 'Phone Number',
      value: customerPhoneNumber.value,
      short: true,
    },
    {
      title: 'Account Creation Date',
      value: customerInfo.value.createdAt,
      short: true,
    },
    {
      title: 'Trips Taken',
      value: (totalOrderInfo.value?.totalRides || 0).toString(),
      short: true,
    },
    {
      title: 'Do they have any outstanding balances on the account?',
      value: unpaidServices.value.length > 0 ? 'Yes' : 'No',
      short: false,
    },
  ];

  if (unpaidServices.value.length > 0) {
    fields.push(
      {
        title: 'If so, how many?',
        value: unpaidServices.value.length.toString(),
        short: true,
      },
      {
        title: 'If so, what is the total amount owed?',
        value: `$${owedTotalAmount.value}`,
        short: true,
      },
    );
  }

  fields.push(
    {
      title: 'Did we take the customer where they are currently at?',
      value: formData.value.isCustomerTakenToCurrentLocationByUs,
      short: false,
    },
    {
      title: 'Are they going home?',
      value: formData.value.goingHome,
      short: false,
    },
    {
      title: 'Have they taken any emergency rides before?',
      value: hasEmergencyRides.value ? `${customerInfo.value.emergencyRides} ride(s)` : 'None',
      short: false,
    },
    {
      title: 'Did you ask for another form of payment?',
      value: formData.value.askedAnotherForm,
      short: false,
    },
    {
      title: 'Reason for emergency ride',
      value: formData.value.emergencyRideReason,
      short: false,
    },
    {
      title: 'Operator',
      value: userFullName.value,
      short: false,
    },
  );

  const postData: SlackPostDataType = {
    slackWebhookUrlKey: 'onlineWebhookUrl',
    title: 'Emergency Ride Request',
    icon: 'alert',
    fields,
  };

  isLoading.value = true;
  try {
    await sendPost('slackWebHook', postData);
    showSuccessPopupDialog({
      text: 'The request has been sent successfully!',
    });
  } catch (error) {
    const errorText = util.getErrorResponse(error, false);
    showErrorPopupDialog({
      text: errorText,
    });
  }
  isLoading.value = false;
};
</script>

<style lang="scss">
#emergency-ride-request {
  .field-value {
    color: #e3210a;
  }
}
</style>
